import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Accordion, AccordionSummary, AccordionDetails,
  TextField, MenuItem, Button, Chip, CircularProgress, Divider, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from 'notistack';
import * as docuRuleApi from '../../lib/api/docuRule';

// 화면 묶음. 서버의 DOCU_GRP 와 짝이 맞아야 한다.
const GROUPS = [
  { key: 'SALES', label: '영업', desc: '고객에게 나가는 문서가 많습니다' },
  { key: 'MANUFACTURING', label: '제조', desc: '대부분 사내에서만 씁니다' },
  { key: 'ETC', label: '기타', desc: '' },
];

const RESET_OPTIONS = [
  { value: 'DAY', label: '매일 1번부터' },
  { value: 'MONTH', label: '매월 1번부터' },
  { value: 'YEAR', label: '매년 1번부터' },
  { value: 'NEVER', label: '계속 이어서' },
];

// 누르면 커서 자리에 꽂히는 토큰들. 설명이 없으면 무엇을 쓸 수 있는지 알 방법이 없다.
const TOKENS = [
  { t: '{PREFIX}', d: '회사 표시' },
  { t: '{TYP}', d: '문서 종류' },
  { t: '{YYYY}', d: '연 4자리' },
  { t: '{YY}', d: '연 2자리' },
  { t: '{MM}', d: '월' },
  { t: '{DD}', d: '일' },
  { t: '{SEQ:3}', d: '일련번호 3자리' },
  { t: '{SEQ:4}', d: '일련번호 4자리' },
];

const DocuRuleContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rules, setRules] = useState([]);
  const [prefix, setPrefix] = useState('');
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});   // docuTyp -> { pattern, seqReset, sample, saving }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await docuRuleApi.getDocuRules();
      setRules(res.data?.list || []);
      setPrefix(res.data?.prefix || '');
      setDrafts({});
    } catch {
      enqueueSnackbar('문서번호 규칙을 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const draftOf = (rule) => drafts[rule.docuTyp] || {
    pattern: rule.pattern || '',
    seqReset: rule.seqReset || 'MONTH',
    sample: rule.sample || '',
  };

  const setDraft = (docuTyp, patch) =>
    setDrafts((d) => ({ ...d, [docuTyp]: { ...d[docuTyp], ...patch } }));

  // 미리보기는 서버가 만든다. 화면에서 따로 조립하면 두 벌이 되고 언젠가 어긋난다.
  const refreshSample = async (rule, pattern) => {
    try {
      const res = await docuRuleApi.previewDocuRule(pattern, rule.docuTyp);
      setDraft(rule.docuTyp, { sample: res.data?.sample || '' });
    } catch {
      setDraft(rule.docuTyp, { sample: '' });
    }
  };

  const handlePatternChange = (rule, value) => {
    setDraft(rule.docuTyp, { ...draftOf(rule), pattern: value });
    refreshSample(rule, value);
  };

  const insertToken = (rule, token) => {
    const cur = draftOf(rule);
    const next = `${cur.pattern}${token}`;
    setDraft(rule.docuTyp, { ...cur, pattern: next });
    refreshSample(rule, next);
  };

  const handleSave = async (rule) => {
    const cur = draftOf(rule);
    setDraft(rule.docuTyp, { ...cur, saving: true });
    try {
      await docuRuleApi.saveDocuRule(rule.docuTyp, {
        pattern: cur.pattern,
        seqReset: cur.seqReset,
      });
      enqueueSnackbar(`${rule.docuNm} 번호 모양을 바꿨습니다.`, { variant: 'success' });
      load();
    } catch (e) {
      const msg = e?.response?.data?.message || '저장에 실패했습니다.';
      enqueueSnackbar(msg, { variant: 'error' });
      setDraft(rule.docuTyp, { ...cur, saving: false });
    }
  };

  const changed = (rule) => {
    const cur = draftOf(rule);
    return cur.pattern !== (rule.pattern || '') || cur.seqReset !== (rule.seqReset || '');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ m: 1 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>번호 앞에 붙는 회사 표시</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={prefix || '설정 안 됨'} color={prefix ? 'primary' : 'default'} size="small" />
          <Typography variant="caption" color="text.secondary">
            {'{PREFIX}'} 자리에 들어갑니다. 공통코드 BASE / COMPANY / DOCU_PREFIX 에서 바꿉니다.
          </Typography>
        </Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        바꾼 모양은 앞으로 만들어지는 번호에만 적용됩니다. 이미 발급된 번호는 그대로 남습니다.
      </Typography>

      {GROUPS.map((g) => {
        const items = rules.filter((r) => r.docuGrp === g.key);
        if (items.length === 0) return null;
        return (
          <Box key={g.key} sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
              <Typography variant="subtitle1" fontWeight={600}>{g.label}</Typography>
              <Typography variant="caption" color="text.secondary">{g.desc}</Typography>
            </Box>

            {items.map((rule) => {
              const cur = draftOf(rule);
              return (
                <Accordion key={rule.docuTyp} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', flexWrap: 'wrap' }}>
                      <Typography sx={{ minWidth: 96 }}>{rule.docuNm}</Typography>
                      <Chip label={rule.docuTyp} size="small" variant="outlined" />
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {rule.sample || rule.pattern}
                      </Typography>
                      {changed(rule) && <Chip label="변경됨" size="small" color="warning" />}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="번호 모양"
                        value={cur.pattern}
                        onChange={(e) => handlePatternChange(rule, e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{ sx: { fontFamily: 'monospace' } }}
                      />

                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {TOKENS.map((tk) => (
                          <Tooltip key={tk.t} title={tk.d}>
                            <Chip
                              label={tk.t}
                              size="small"
                              variant="outlined"
                              onClick={() => insertToken(rule, tk.t)}
                              sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                            />
                          </Tooltip>
                        ))}
                      </Box>

                      <Divider />

                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          select
                          label="일련번호"
                          value={cur.seqReset}
                          onChange={(e) => setDraft(rule.docuTyp, { ...cur, seqReset: e.target.value })}
                          size="small"
                          sx={{ minWidth: 160 }}
                        >
                          {RESET_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </TextField>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            이렇게 나옵니다
                          </Typography>
                          <Typography sx={{ fontFamily: 'monospace' }}>
                            {cur.sample || '—'}
                          </Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1 }} />

                        <Button
                          variant="contained"
                          size="small"
                          disabled={!changed(rule) || cur.saving}
                          onClick={() => handleSave(rule)}
                        >
                          {cur.saving ? <CircularProgress size={18} /> : '저장'}
                        </Button>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default DocuRuleContainer;
